import React, {
  FC,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { ApolloError } from '@apollo/client';

import { IBaseProps } from '@rbi-ctg/frontend';
import { actions, selectors, useAppDispatch, useAppSelector } from 'state/global-state';
import { getAppliedRewardsFromStorage } from 'state/global-state/models/loyalty/rewards/rewards.utils';
import { useLoyaltyUser } from 'state/loyalty/hooks/use-loyalty-user';
import { IEngineRewardsMap, ISanityRewardsMap, LoyaltyReward } from 'state/loyalty/types';
import { logger } from 'utils/logger';
import noop from 'utils/noop';

import { useEnableLoyaltyServiceRewards } from '../hooks/use-enable-loyalty-service-rewards';
import { useLoyaltyServiceRewards } from '../hooks/use-loyalty-service-rewards';
import { RewardsMap } from '../types';

export interface ServiceRewardsContextValue {
  rewardCategories: ReturnType<typeof useLoyaltyServiceRewards>['rewardCategories'];
  allRewards: LoyaltyReward[];
  rewardsMap: RewardsMap;
  sanityRewardsMap: ISanityRewardsMap;
  engineRewardsMap: IEngineRewardsMap;
  error?: ApolloError;
  loading: boolean;
  refreshRewards: () => Promise<void>;
  activateBrowse: () => void;
  deactivateBrowse: () => void;
}

const defaultServiceRewardsContextValue: ServiceRewardsContextValue = {
  rewardCategories: [],
  allRewards: [],
  rewardsMap: {},
  sanityRewardsMap: {},
  engineRewardsMap: {},
  loading: false,
  refreshRewards: async () => undefined,
  activateBrowse: noop,
  deactivateBrowse: noop,
};

export const ServiceRewardsContext = createContext<ServiceRewardsContextValue>(
  defaultServiceRewardsContextValue
);

export const useServiceRewards = () => useContext(ServiceRewardsContext);

export const useEnableServiceRewardsBrowse = () => {
  const { activateBrowse, deactivateBrowse } = useServiceRewards();

  useEffect(() => {
    activateBrowse();
    return deactivateBrowse;
  }, [activateBrowse, deactivateBrowse]);
};

export const ServiceRewardsProvider: FC<IBaseProps> = ({ children }) => {
  const { enabled } = useEnableLoyaltyServiceRewards();

  return <ServiceRewardsProviderInner enabled={enabled}>{children}</ServiceRewardsProviderInner>;
};

export const ServiceRewardsProviderInner: FC<IBaseProps & { enabled: boolean }> = ({
  children,
  enabled,
}) => {
  const [browseActiveCount, setBrowseActiveCount] = useState(0);
  const activateBrowse = useCallback(() => setBrowseActiveCount(count => count + 1), []);
  const deactivateBrowse = useCallback(
    () => setBrowseActiveCount(count => Math.max(0, count - 1)),
    []
  );
  const browseEnabled = enabled && browseActiveCount > 0;

  const appliedRewards = useAppSelector(selectors.loyalty.selectAppliedRewardsParsed);
  const appliedRewardIds = useMemo(
    () =>
      Array.from(
        new Set([
          ...(appliedRewards?.map(reward => reward.rewardId).filter(Boolean) ?? []),
          ...Object.values(getAppliedRewardsFromStorage())
            .filter(reward => reward.timesApplied > 0)
            .map(reward => reward.rewardId)
            .filter(Boolean),
        ])
      ),
    [appliedRewards]
  );
  const appliedEnabled = enabled && appliedRewardIds.length > 0;

  const browse = useLoyaltyServiceRewards({ skip: !browseEnabled });
  const applied = useLoyaltyServiceRewards({
    rewardIds: appliedRewardIds,
    skip: !appliedEnabled,
  });

  const sanityRewardsMap = useMemo(
    () => ({ ...applied.sanityRewardsMap, ...browse.sanityRewardsMap }),
    [applied.sanityRewardsMap, browse.sanityRewardsMap]
  );
  const rewardsMap = useMemo(() => ({ ...applied.rewardsMap, ...browse.rewardsMap }), [
    applied.rewardsMap,
    browse.rewardsMap,
  ]);
  const engineRewardsMap = useMemo(
    () => ({ ...applied.engineRewardsMap, ...browse.engineRewardsMap }),
    [applied.engineRewardsMap, browse.engineRewardsMap]
  );
  const allRewards = useMemo(() => Object.values(sanityRewardsMap), [sanityRewardsMap]);
  const refreshBrowseRewards = browse.refreshRewards;
  const refreshAppliedRewards = applied.refreshRewards;

  const refreshRewards = useCallback(async () => {
    const refreshes: Promise<void>[] = [];
    if (browseEnabled) {
      refreshes.push(refreshBrowseRewards());
    }
    if (appliedEnabled) {
      refreshes.push(refreshAppliedRewards());
    }
    await Promise.all(refreshes);
  }, [appliedEnabled, browseEnabled, refreshAppliedRewards, refreshBrowseRewards]);

  const dispatch = useAppDispatch();
  useEffect(() => {
    if (enabled) {
      dispatch(actions.loyalty.createAvailableLoyaltyRewardsMap(Object.values(engineRewardsMap)));
    }
  }, [dispatch, enabled, engineRewardsMap]);

  const { loyaltyUser } = useLoyaltyUser();
  const points = loyaltyUser?.points;
  const previousPointsRef = useRef(points);
  useEffect(() => {
    if (previousPointsRef.current === points) {
      return;
    }
    previousPointsRef.current = points;
    if (points === undefined || (!browseEnabled && !appliedEnabled)) {
      return;
    }
    void refreshRewards().catch(error => {
      logger.warn({ error, message: 'Failed to refresh active loyalty-service rewards' });
    });
  }, [appliedEnabled, browseEnabled, points, refreshRewards]);

  const value: ServiceRewardsContextValue = enabled
    ? {
        rewardCategories: browse.rewardCategories,
        allRewards,
        rewardsMap,
        sanityRewardsMap,
        engineRewardsMap,
        error:
          (browseEnabled ? browse.error : undefined) ||
          (appliedEnabled ? applied.error : undefined),
        loading: !browseEnabled || browse.loading,
        refreshRewards,
        activateBrowse,
        deactivateBrowse,
      }
    : defaultServiceRewardsContextValue;

  return <ServiceRewardsContext.Provider value={value}>{children}</ServiceRewardsContext.Provider>;
};
